import { useState, useMemo, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SheetSearchSelect } from "../smart-gt/SmartGTComponents"
import { Pagination } from "@/components/ui/pagination"
import { Separator } from "@/components/ui/separator"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { ROUTE_CODE_MAX } from "@cdc-sccr/shared"
import { PlusIcon, PencilIcon, Trash2Icon, SearchIcon, ArrowRightIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

// ─── Types ────────────────────────────────────────────────────────────────────

interface Country  { id: string; name: string; isoCode: string }
interface StateRow { id: string; name: string; countryId: string }
interface CityRow  { id: string; name: string; code?: string | null; stateId: string; stateName: string; countryId: string; countryName: string }

interface RouteRow {
  id: string
  code: string
  name: string
  km?: number | null
  deletedAt?: string | null
  originCityId: string
  originCityName: string
  originCityCode?: string | null
  originStateName: string
  originCountryName: string
  targetCityId: string
  targetCityName: string
  targetCityCode?: string | null
  targetStateName: string
  targetCountryName: string
}

const PAGE_SIZE = 25

type RouteForm = {
  code: string
  name: string
  originCityId: string
  targetCityId: string
  km: string
  deleted: boolean
}

const EMPTY: RouteForm = {
  code: "", name: "",
  originCityId: "", targetCityId: "",
  km: "",
  deleted: false,
}

// ─── Selector de ciudad (país → provincia → ciudad) ───────────────────────────

function CitySelector({
  label,
  value,
  onChange,
  countries,
  allStates,
  allCities,
}: {
  label: string
  value: string
  onChange: (cityId: string) => void
  countries: Country[]
  allStates: StateRow[]
  allCities: CityRow[]
}) {
  const [country, setCountry] = useState("")
  const [state,   setState]   = useState("")

  // Pre-fill cuando se edita
  useEffect(() => {
    if (!value) { setCountry(""); setState(""); return }
    const city = allCities.find(c => c.id === value)
    if (city) {
      setCountry(city.countryId)
      setState(city.stateId)
    }
  }, [value, allCities])

  const filteredStates = country ? allStates.filter(s => s.countryId === country) : allStates
  const filteredCities = state   ? allCities.filter(c => c.stateId   === state)   : []

  const selCountry = countries.find(c => c.id === country)?.name
  const selState   = allStates.find(s => s.id === state)?.name
  const selCity    = allCities.find(c => c.id === value)?.name

  return (
    <div className="grid gap-2">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>

      {/* País */}
      <Select value={country} onValueChange={v => { setCountry(v ?? ""); setState(""); onChange("") }}>
        <SelectTrigger size="sm">
          <SelectValue placeholder="País">
            {selCountry ?? (country ? country : null)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Provincia */}
      <Select value={state} onValueChange={v => { setState(v ?? ""); onChange("") }} disabled={!country}>
        <SelectTrigger size="sm">
          <SelectValue placeholder="Provincia">
            {selState ?? (state ? state : null)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {filteredStates.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Ciudad (autocomplete con código) */}
      <div className={!state ? "opacity-50 pointer-events-none" : undefined}>
        <SheetSearchSelect
          value={value}
          onChange={(v) => onChange(v)}
          items={filteredCities.map(c => ({
            value: c.id,
            label: c.code ? `${c.code} · ${c.name} (${c.stateName})` : `${c.name} (${c.stateName})`,
          }))}
          placeholder="Ciudad..."
          label="Ciudad"
        />
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RoutesPage() {
  const qc      = useQueryClient()
  const { can } = usePermissions()

  const [search,      setSearch]      = useState("")
  const [filterOrigin, setFilterOrigin] = useState("")
  const [filterDest,   setFilterDest]   = useState("")
  const [filterStatus, setFilterStatus] = useState<"active" | "deleted" | "all">("active")
  const [page,         setPage]         = useState(1)
  const [sheetOpen,  setSheetOpen]  = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editing,    setEditing]    = useState<RouteRow | null>(null)
  const [deleting,   setDeleting]   = useState<RouteRow | null>(null)
  const [form,       setForm]       = useState({ ...EMPTY })
  const [formError,  setFormError]  = useState<string | null>(null)

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: countries = [] } = useQuery({
    queryKey: ["geo", "countries"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/countries`)
      return r.json() as Promise<Country[]>
    },
  })

  const { data: allStates = [] } = useQuery({
    queryKey: ["geo", "states"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/states`)
      return r.json() as Promise<StateRow[]>
    },
  })

  const { data: allCities = [] } = useQuery({
    queryKey: ["geo", "cities"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/cities`)
      return r.json() as Promise<CityRow[]>
    },
  })

  const { data: allRoutes = [], isLoading } = useQuery({
    queryKey: ["geo", "routes"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/routes`)
      return r.json() as unknown as Promise<RouteRow[]>
    },
  })

  // ── Filtrado + paginación ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term   = search.toLowerCase().trim()
    const origin = filterOrigin.toLowerCase().trim()
    const dest   = filterDest.toLowerCase().trim()
    return allRoutes.filter(r => {
      const matchSearch = !term ||
        r.code.toLowerCase().includes(term) ||
        r.name.toLowerCase().includes(term) ||
        r.originCityName.toLowerCase().includes(term) ||
        r.targetCityName.toLowerCase().includes(term)
      const matchOrigin = !origin ||
        r.originCityName.toLowerCase().includes(origin) ||
        (r.originCityCode ?? "").toLowerCase().includes(origin)
      const matchDest = !dest ||
        r.targetCityName.toLowerCase().includes(dest) ||
        (r.targetCityCode ?? "").toLowerCase().includes(dest)
      const matchStatus =
        filterStatus === "all"
          ? true
          : filterStatus === "active"
            ? !r.deletedAt
            : Boolean(r.deletedAt)
      return matchSearch && matchOrigin && matchDest && matchStatus
    })
  }, [allRoutes, search, filterOrigin, filterDest, filterStatus])

  useEffect(() => { setPage(1) }, [search, filterOrigin, filterDest, filterStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: { code: string; name: string; originCityId: string; targetCityId: string; km?: number; deletedAt?: string | null }) => {
      const res = await api.geo.routes.$post({ json: data })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "routes"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { code: string; name: string; originCityId: string; targetCityId: string; km?: number; deletedAt?: string | null } }) => {
      const res = await api.geo.routes[":id"].$put({ param: { id }, json: data })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "routes"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.geo.routes[":id"].$delete({ param: { id } })
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error") }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "routes"] }); setDeleteOpen(false); setDeleting(null) },
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/geo/routes/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "routes"] }) },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  function closeSheet() { setSheetOpen(false); setEditing(null); setForm({ ...EMPTY }); setFormError(null) }

  function openCreate() { setEditing(null); setForm({ ...EMPTY }); setFormError(null); setSheetOpen(true) }

  function openEdit(route: RouteRow) {
    setEditing(route)
    setForm({
      code: route.code,
      name: route.name,
      originCityId: route.originCityId,
      targetCityId: route.targetCityId,
      km: route.km == null ? "" : String(route.km),
      deleted: Boolean(route.deletedAt),
    })
    setFormError(null); setSheetOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setFormError(null)
    if (!form.originCityId || !form.targetCityId) {
      setFormError("Seleccioná la ciudad de origen y destino"); return
    }
    const km = form.km.trim() === "" ? undefined : Number(form.km)
    if (form.km.trim() !== "" && (!Number.isFinite(km) || Number(km) < 0)) {
      setFormError("KM debe ser un número entero mayor o igual a 0"); return
    }
    const payload = {
      code: form.code,
      name: form.name,
      originCityId: form.originCityId,
      targetCityId: form.targetCityId,
      ...(km == null ? {} : { km: Math.trunc(km) }),
      deletedAt: form.deleted ? new Date().toISOString() : null,
    }
    if (editing) updateMut.mutate({ id: editing.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Rutas" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">

        {/* Barra de herramientas */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {/* Búsqueda general */}
          <div className="relative flex-1 min-w-[180px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Código, nombre o ciudad..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Filtro origen */}
          <div className="relative w-44">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Origen..."
              value={filterOrigin}
              onChange={e => setFilterOrigin(e.target.value)}
              className="pl-8 text-sm"
            />
          </div>

          {/* Filtro destino */}
          <div className="relative w-44">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Destino..."
              value={filterDest}
              onChange={e => setFilterDest(e.target.value)}
              className="pl-8 text-sm"
            />
          </div>
          <div className="w-44">
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as "active" | "deleted" | "all")}>
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="deleted">Borrados</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {can("write", "geo") && (
            <Button onClick={openCreate} className="gap-2 shrink-0">
              <PlusIcon className="size-4" /> Nueva ruta
            </Button>
          )}
        </div>

        {/* Tabla */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead className="w-6"></TableHead>
                <TableHead>Destino</TableHead>
                <TableHead className="w-24 text-right">KM</TableHead>
                {can("write", "geo") && <TableHead className="text-right pr-4 w-24">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={can("write", "geo") ? 7 : 6} className="text-center py-16 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={can("write", "geo") ? 7 : 6} className="text-center py-16 text-muted-foreground">
                  {search ? "Sin resultados para la búsqueda" : "No hay rutas registradas"}
                </TableCell></TableRow>
              ) : paged.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{r.code}</span>
                  </TableCell>
                  <TableCell className="font-medium text-sm">{r.name}</TableCell>
                  <TableCell className="text-sm">
                    <span className="block font-medium">{r.originCityName}</span>
                    {r.originCityCode && (
                      <span className="block font-mono text-xs text-muted-foreground">{r.originCityCode}</span>
                    )}
                    <span className="block text-muted-foreground text-xs">
                      {r.originStateName} · {r.originCountryName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <ArrowRightIcon className="size-3.5 text-muted-foreground" />
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="block font-medium">{r.targetCityName}</span>
                    {r.targetCityCode && (
                      <span className="block font-mono text-xs text-muted-foreground">{r.targetCityCode}</span>
                    )}
                    <span className="block text-muted-foreground text-xs">
                      {r.targetStateName} · {r.targetCountryName}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-right tabular-nums">
                    {r.km ?? "-"}
                  </TableCell>
                  {can("write", "geo") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {r.deletedAt ? (
                          <Button variant="ghost" size="icon-sm"
                            onClick={() => restoreMut.mutate(r.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Restaurar">
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(r)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon-sm"
                              onClick={() => { setDeleting(r); setDeleteOpen(true) }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Eliminar">
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Sheet: Crear / Editar */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar ruta" : "Nueva ruta"}</SheetTitle>
            <SheetDescription>Código único, nombre y ciudades de origen y destino.</SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
            {/* Código + Nombre */}
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="r-code">Código</Label>
                <Input id="r-code" value={form.code} maxLength={ROUTE_CODE_MAX}
                  className="font-mono uppercase" placeholder="BSAS-ROS"
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  required />
              </div>
              <div className="grid gap-1.5 col-span-1" style={{ gridColumn: "1 / -1" }}>
                <Label htmlFor="r-name">Nombre</Label>
                <Input id="r-name" value={form.name}
                  placeholder="Buenos Aires → Rosario"
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="r-km">KM</Label>
                <Input
                  id="r-km"
                  type="number"
                  min={0}
                  step={1}
                  value={form.km}
                  placeholder="0"
                  onChange={e => setForm(f => ({ ...f, km: e.target.value }))}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="r-deleted">Deleted</Label>
                <Select
                  value={form.deleted ? "true" : "false"}
                  onValueChange={(v) => setForm(f => ({ ...f, deleted: v === "true" }))}
                >
                  <SelectTrigger id="r-deleted" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="true">Sí (hoy)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Ciudad Origen */}
            <CitySelector
              label="🟢 Ciudad Origen"
              value={form.originCityId}
              onChange={v => setForm(f => ({ ...f, originCityId: v }))}
              countries={countries}
              allStates={allStates}
              allCities={allCities}
            />

            <Separator />

            {/* Ciudad Destino */}
            <CitySelector
              label="🔴 Ciudad Destino"
              value={form.targetCityId}
              onChange={v => setForm(f => ({ ...f, targetCityId: v }))}
              countries={countries}
              allStates={allStates}
              allCities={allCities}
            />

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear ruta"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Dialog: Confirmar eliminación */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar ruta</DialogTitle>
            <DialogDescription>
              ¿Eliminás la ruta <strong>{deleting?.code}</strong> — {deleting?.name}?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button></DialogClose>
            <Button variant="destructive" disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}>
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
